import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
export const LOCATION_SEARCH_RESULT_COLORS = {
  pageBg: '#F7F8FA',
  paperInnerBg: '#F2F2F2',
  teal: '#255E5E',
  tealStatDesktop: '#005F54',
  closestBadge: '#05A883',
  textPrimary: '#28333A',
  textSecondary: '#3F4040',
  bullet: '#757575',
  chipBg: '#EEF0F2',
  border: '#CFCFD0',
  white: '#FFFFFF',
} as const;

export const StResultsSection = styled('section')(({ theme }) => ({
  boxSizing: 'border-box',
  width: '100%',
  backgroundColor: LOCATION_SEARCH_RESULT_COLORS.pageBg,
  [theme.breakpoints.up('lg')]: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '48px 41px',
    gap: '32px',
  },
}));

export const StResultsPanel = styled(Box)(({ theme }) => ({
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  width: '100%',
  backgroundColor: LOCATION_SEARCH_RESULT_COLORS.pageBg,
  [theme.breakpoints.down('lg')]: {
    padding: '24px 0 32px',
    gap: '20px',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '16px 0 24px',
  },
  [theme.breakpoints.up('lg')]: {
    width: '100%',
    maxWidth: '1264px',
    margin: '0 auto',
  },
}));

export const StPaperContainer = styled(Box)(({ theme }) => ({
  boxSizing: 'border-box',
  width: '100%',
  borderRadius: '10px',
  filter: 'drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.25))',
  backgroundColor: LOCATION_SEARCH_RESULT_COLORS.paperInnerBg,
  overflow: 'hidden',
  [theme.breakpoints.up('lg')]: {
    display: 'flex',
    flexDirection: 'row',
    minHeight: '975px',
  },
}));

export const StResultsList = styled(Box)(({ theme }) => ({
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '18px',
  [theme.breakpoints.up('lg')]: {
    width: '464px',
    flexShrink: 0,
    padding: '26px 28px',
    overflowY: 'auto',
    maxHeight: '975px',
  },
  [theme.breakpoints.down('lg')]: {
    width: '100%',
    gap: '12px',
    padding: '0 32px',
    boxSizing: 'border-box',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '0 16px',
  },
}));

export const StMapPanel = styled(Box)(({ theme }) => ({
  boxSizing: 'border-box',
  flex: 1,
  minWidth: 0,
  minHeight: '380px',
  width: '100%',
  '.gm-style-iw-c': {
    padding: '0px',
    overflow: 'visible',
    width: '250px ',
  },
  '.gm-style-iw-d': {
    overflow: 'visible',
  },
  '.gm-style-iw-d + button': {
    display: 'none',
  },
  '.gm-style-iw-chr': {
    display: 'none',
  },
  [theme.breakpoints.down('lg')]: {
    '.gm-style-iw-c': {
      top: '-54px',
    },
    width: '100%',
    maxWidth: '100%',
  },
  [theme.breakpoints.up('lg')]: {
    minHeight: '975px',
  },
}));

export const StMapStatusMessage = styled(Typography)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  height: '100%',
  fontFamily: 'Noto Sans, sans-serif',
  fontSize: '16px',
  lineHeight: '24px',
  color: LOCATION_SEARCH_RESULT_COLORS.textSecondary,
});

export const StMapBlock = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '380px',
  [theme.breakpoints.up('lg')]: {
    height: '100%',
    minHeight: '380px',
  },
}));

export const StSiteCard = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'selected',
})<{ selected?: boolean }>(({ selected }) => ({
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  padding: '24px',
  gap: '16px',
  width: '100%',
  background: LOCATION_SEARCH_RESULT_COLORS.white,
  borderRadius: '12px',
  border: selected ? `2px solid ${LOCATION_SEARCH_RESULT_COLORS.teal}` : '2px solid transparent',
  cursor: selected === undefined ? 'default' : 'pointer',
}));

export const StCardHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  width: '100%',
  [theme.breakpoints.down('lg')]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '14px',
  },
}));

export const StSiteInfo = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '4px',
  flex: 1,
  minWidth: 0,
});

export const StSiteTitle = styled(Typography)({
  fontFamily: 'Noto Sans, sans-serif',
  fontStyle: 'normal',
  fontWeight: 700,
  fontSize: '16px',
  lineHeight: '24px',
  color: LOCATION_SEARCH_RESULT_COLORS.textPrimary,
});

export const StSiteSelectTitle = styled('button')({
  fontFamily: 'Noto Sans, sans-serif',
  fontStyle: 'normal',
  fontWeight: 700,
  fontSize: '16px',
  lineHeight: '24px',
  color: LOCATION_SEARCH_RESULT_COLORS.textPrimary,
  border: 'none',
  background: 'none',
  padding: 0,
  margin: 0,
  textAlign: 'left',
  cursor: 'pointer',
  width: '100%',
});

export const StLocationRow = styled(Box)({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
});

export const StLocationPinWrap = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  padding: '0px 4px 0px 0px',
  width: '20px',
  height: '16px',
});

export const StLocationText = styled(Typography)({
  fontFamily: 'Noto Sans, sans-serif',
  fontStyle: 'normal',
  fontWeight: 400,
  fontSize: '13px',
  lineHeight: '20px',
  letterSpacing: '0.26px',
  color: LOCATION_SEARCH_RESULT_COLORS.textSecondary,
});

export const StLocationBullet = styled('span')({
  fontFamily: 'Noto Sans, sans-serif',
  fontStyle: 'normal',
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '20px',
  color: LOCATION_SEARCH_RESULT_COLORS.bullet,
  padding: '0px 8px',
});

export const StClosestBadge = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  padding: '4px 8px',
  background: LOCATION_SEARCH_RESULT_COLORS.closestBadge,
  borderRadius: '9999px',
  flexShrink: 0,
  maxWidth: '62px',
});

export const StClosestBadgeText = styled(Typography)({
  fontFamily: 'Noto Sans, sans-serif',
  fontStyle: 'normal',
  fontWeight: 700,
  fontSize: '10px',
  lineHeight: '15px',
  letterSpacing: '0.5px',
  textTransform: 'uppercase',
  color: LOCATION_SEARCH_RESULT_COLORS.white,
});

export const StTagsRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  alignItems: 'flex-start',
  alignContent: 'flex-start',
  gap: '8px',
  width: '100%',
  '& .MuiButton-root.Mui-focusVisible, & .MuiButton-root:focus-visible': {
    outline: `2px solid ${theme.palette.common.black}`,
    outlineOffset: '2px',
  },
}));

export const StConditionTag = styled(Box)({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
  padding: '6px 12px',
  background: LOCATION_SEARCH_RESULT_COLORS.chipBg,
  borderRadius: '20px',
});

export const StConditionTagText = styled(Typography)({
  fontFamily: 'Noto Sans, sans-serif',
  fontStyle: 'normal',
  fontWeight: 500,
  fontSize: '14px',
  lineHeight: '19px',
  color: LOCATION_SEARCH_RESULT_COLORS.textPrimary,
});

export const StStatsRow = styled(Box)(({ theme }) => ({
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'row',
  width: '100%',
  height: '68px',
  borderTop: `1px solid ${LOCATION_SEARCH_RESULT_COLORS.border}`,
  paddingTop: '9px',
  gap: '16px',
  [theme.breakpoints.down('lg')]: {
    '& > *': {
      flex: 1,
    },
  },
}));

export const StStatColumn = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  width: '128px',
  [theme.breakpoints.down('lg')]: {
    width: 'auto',
    flex: 1,
  },
}));

export const StStatNumber = styled(Typography)(({ theme }) => ({
  fontFamily: 'Noto Sans, sans-serif',
  fontStyle: 'normal',
  fontWeight: 700,
  fontSize: '24px',
  lineHeight: '32px',
  color: LOCATION_SEARCH_RESULT_COLORS.tealStatDesktop,
  [theme.breakpoints.down('lg')]: {
    color: LOCATION_SEARCH_RESULT_COLORS.teal,
  },
}));

export const StStatLabel = styled(Typography)({
  fontFamily: 'Noto Sans, sans-serif',
  fontStyle: 'normal',
  fontWeight: 700,
  fontSize: '13px',
  lineHeight: '20px',
  color: LOCATION_SEARCH_RESULT_COLORS.textSecondary,
});

export const StButtonWrap = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '10px',
  width: '100%',
  '& .MuiButton-root.Mui-focusVisible, & .MuiButton-root:focus-visible': {
    outline: `2px solid ${theme.palette.common.black}`,
    outlineOffset: '2px',
  },
}));

export const StInactiveButtonWrap = styled(StButtonWrap)(({ theme }) => ({
  padding: '8px 0px',
  '& .MuiButton-root.Mui-focusVisible, & .MuiButton-root:focus-visible': {
    outline: `2px solid ${theme.palette.common.black}`,
    outlineOffset: '2px',
  },
}));

export const StStatusMessage = styled(Typography)({
  fontFamily: 'Noto Sans, sans-serif',
  fontSize: '16px',
  lineHeight: '24px',
  color: LOCATION_SEARCH_RESULT_COLORS.textSecondary,
  padding: '24px 32px',
});

export const StShowMoreWrap = styled(Box)(({ theme }) => ({
  boxSizing: 'border-box',
  display: 'flex',
  justifyContent: 'center',
  width: '100%',
  padding: '16px 0',
  borderTop: `1px solid ${LOCATION_SEARCH_RESULT_COLORS.border}`,
  [theme.breakpoints.up('lg')]: {
    display: 'none',
  },
  '& .MuiButton-root.Mui-focusVisible, & .MuiButton-root:focus-visible': {
    outline: `2px solid ${theme.palette.common.black}`,
    outlineOffset: '2px',
  },
}));

export const StMapInfoWindow = styled(Box)({
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  padding: '16px',
  gap: '8px',
  width: '100%',
  maxWidth: '250px',
  background: LOCATION_SEARCH_RESULT_COLORS.white,
  borderRadius: '24px',
});

export const StMapInfoHeader = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: '8px',
  width: '100%',
});

export const StMapInfoTitle = styled('div')({
  fontFamily: 'Noto Sans, sans-serif',
  fontWeight: 700,
  fontSize: '16px',
  lineHeight: '22px',
  color: LOCATION_SEARCH_RESULT_COLORS.textPrimary,
  flex: 1,
  minWidth: 0,
});

export const StMapInfoCloseButton = styled(IconButton)({
  flexShrink: 0,
  margin: '-4px -8px 0 0',
  padding: '4px',
  color: LOCATION_SEARCH_RESULT_COLORS.textPrimary,
});

export const StMapInfoDivider = styled(Box)({
  width: '100%',
  height: '1px',
  background: LOCATION_SEARCH_RESULT_COLORS.border,
});

export const StMapInfoAddress = styled(Typography)({
  fontFamily: 'Noto Sans, sans-serif',
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '20px',
  color: LOCATION_SEARCH_RESULT_COLORS.textPrimary,
  width: '100%',
});

export const StMapInfoMetaRow = styled(Typography)({
  fontFamily: 'Noto Sans, sans-serif',
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '20px',
  color: LOCATION_SEARCH_RESULT_COLORS.textSecondary,
  width: '100%',
});

export const StMapInfoButtonWrap = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  width: '100%',
  marginTop: '4px',
  '& a': {
    width: 'auto',
    minWidth: '120px',
  },
});
